
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';

import { AppComponent } from './app.component';

/** angularfire2*/
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getStorage, provideStorage } from '@angular/fire/storage';

/**firebase congfig */
import { FirebaseConfig, recaptchaEnterprise } from '../environments/environment';
import {
    ActiveAnonymousChatService,
    ActivePickupService,
    AirportService,
    ArrivalRequestService,
    AvgServeTimeService,
    AwbHistoryService,
    AwbService,
    CommonNotificationEmailsService,
    CurrentUserService,
    DEVICE,
    DockDoorService,
    DriverService,
    DynamicFormsService,
    DynamicPriorityService,
    FaviconService,
    FireStoreService,
    FrontCounterQuickMessageService,
    GenericCatalogService,
    GoogleChartComponent,
    HawbService,
    KioskConfigService,
    KioskFieldService,
    MESAGGING,
    MessagingService,
    NotificationsStatusService,
    OrganizationService,
    PayService,
    PickupAttendingAgentsService,
    PickupCommentService,
    PickupHistoryService,
    PickupListFrontCounterService,
    PickupListSharedService,
    PickupListUtilsService,
    PickupListWarehouseService,
    PickupSortingService,
    PrintService,
    RejectedReasonService,
    SOURCE_APP,
    SessionService,
    TruckerService,
    UserService,
    WarehouseOptionsService,
    WarehouseService
} from './utils';

/**
  @APP ROUTING
**/
import { AppRoutingModule } from './app.routes';

/**
  @CUSTOM MODULES
**/
import { AuthModule } from './auth/auth.module';
import { CargoFacilityModule } from './cargo-facility/cargo-facility.module';
import { StatusComponent } from './status/status.component';

import { CsModalModule } from '@cargos/cs-modal';
import { NetworkConnectionModule } from './shared/modules/network-connection';
import { TerminalsModule } from './terminals/terminals.module';

import { provideAppCheck } from '@angular/fire/app-check';
import device from 'current-device';
import { ReCaptchaEnterpriseProvider, initializeAppCheck } from 'firebase/app-check';
import { provideNgxMask } from 'ngx-mask';
import { AppErrorHandler } from './app-error.handler';
import { PublicActionsModule } from './public-actions/public-actions.module';
import { generateDeviceUUID } from './shared/factories/web-device-uuid-generator';
import { createHashUint8Array } from './shared/factories/web-hash-creator';
import { AcceptanceLegalTermsModule } from './shared/modules/acceptance-legal-terms/acceptance-legal-terms.module';
import { EnvIndicatorModule } from './shared/modules/env-indicator/env-indicator.module';
import { ChromeBoxMessagesService } from './shared/services/chrome-box-messages.service';
import { ngxMaskConfig } from './shared/services/ngx-mask/ngx-mask.config';
import { AppReleaseService } from './utils/cargo-driver-check-in-service';
import { DEVICE_UUID_GENERATOR, DeviceUuidGenerator, HASH_CREATOR, HashCreator } from './utils/cargo-driver-check-in-service/const';
import { LocalWebStorage } from './utils/local-storage/local-web-storage';

@NgModule({
    declarations: [
        AppComponent,
        StatusComponent
    ],
    exports: [
        RouterModule
    ],
    bootstrap: [
        AppComponent
    ],
    imports: [BrowserModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AuthModule,
        CargoFacilityModule,
        TerminalsModule,
        NetworkConnectionModule,
        CsModalModule,
        EnvIndicatorModule,
        AcceptanceLegalTermsModule,
        PublicActionsModule
    ],
    providers: [
        provideFirebaseApp(() => initializeApp(FirebaseConfig)),
        provideAppCheck(() => initializeAppCheck(getApp(), {
            provider: new ReCaptchaEnterpriseProvider(recaptchaEnterprise.siteKey),
            isTokenAutoRefreshEnabled: true
        })),
        provideFirestore(() => getFirestore()),
        provideAuth(() => getAuth()),
        provideStorage(() => {
            const storage = getStorage();
            storage.maxUploadRetryTime = 300_000;
            return storage;
        }),
        provideMessaging(() => getMessaging()),
        AppReleaseService,
        FireStoreService,
        SessionService,
        UserService,
        PickupCommentService,
        RejectedReasonService,
        FaviconService,
        DynamicFormsService,
        MessagingService,
        HawbService,
        NotificationsStatusService,
        DynamicPriorityService,
        WarehouseOptionsService,
        GenericCatalogService,
        CurrentUserService,
        PickupAttendingAgentsService,
        PickupListUtilsService,
        PickupListWarehouseService,
        PickupListFrontCounterService,
        PickupListSharedService,
        PickupSortingService,
        ActivePickupService,
        AvgServeTimeService,
        ArrivalRequestService,
        FrontCounterQuickMessageService,
        ActiveAnonymousChatService,
        PickupHistoryService,
        CommonNotificationEmailsService,
        DriverService,
        TruckerService,
        OrganizationService,
        WarehouseService,
        AirportService,
        AwbService,
        DockDoorService,
        TruckerService,
        AwbHistoryService,
        PrintService,
        PayService,
        GoogleChartComponent,
        KioskFieldService,
        KioskConfigService,
        { provide: DEVICE, useValue: device },
        { provide: MESAGGING, useClass: LocalWebStorage },
        { provide: SOURCE_APP, useValue: 'website' },
        {
            provide: HASH_CREATOR,
            useFactory: (): HashCreator => {
                return createHashUint8Array;
            }
        },
        {
            provide: DEVICE_UUID_GENERATOR,
            useFactory: (chromeBoxMessagesService: ChromeBoxMessagesService, sessionService: SessionService): DeviceUuidGenerator => {
                return () => generateDeviceUUID(chromeBoxMessagesService, sessionService);
            },
            deps: [ChromeBoxMessagesService, SessionService]
        },
        ...provideNgxMask(ngxMaskConfig),
        provideHttpClient(withInterceptorsFromDi()),
        { provide: ErrorHandler, useClass: AppErrorHandler },
    ]
})
export class AppModule { }
